import {Either, isLeft, right} from 'fp-ts/lib/Either'
import * as t from 'io-ts'
import {set} from 'object-path-immutable'
import {emptyArray} from '@atomic.io/common/types/index'

/** This file holds the logic needed to verify, load / migrate a SFMC Journey config.json */

export const PayloadCodecV0 = t.partial({
  arguments: t.partial({
    execute: t.partial({
      inArguments: t.array(t.record(t.string, t.string)),
      outArguments: t.unknown,
      url: t.string,
      body: t.string,
      header: t.string,
      verb: t.literal('POST'),
      format: t.literal('json'),
      useJwt: t.boolean,
      timeout: t.number
    })
  }),
  metaData: t.partial({
    isConfigured: t.boolean,
    // custom properties, nested under atomic_io to avoid clashes with any SFMC properties
    atomic_io: t.partial({
      organisationId: t.string,
      environmentId: t.string,
      actionFlowId: t.string
    })
  })
})
export type PayloadV0 = t.TypeOf<typeof PayloadCodecV0>

const InArgsV1 = t.intersection([
  t.partial({
    // The 'variables' value type of 'record' means we won't be a valid OldInArgs
    // which we want, as it allows us to differentiate between the two
    variables: t.record(t.string, t.string),
    targetUserIds: t.array(t.string)
  }),
  t.type({
    schemaVersion: t.literal('1')
  })
])

export type InArgsV1 = t.TypeOf<typeof InArgsV1>

const UnConfiguredConfigCodec = t.partial({
  arguments: t.partial({
    execute: t.partial({
      inArguments: emptyArray, // if an older version of the plugin is loaded with empty args (not yet populated)
      outArguments: t.unknown,
      url: t.string,
      body: t.string,
      header: t.string,
      verb: t.literal('POST'),
      format: t.literal('json'),
      useJwt: t.boolean,
      timeout: t.number
    })
  }),
  metaData: t.partial({
    isConfigured: t.boolean
  })
})

// Note this is incomplete, we ignore and just pass back anything that the
// plugin isn't concerned with
const ConfiguredPayloadCodecV1 = t.partial({
  arguments: t.partial({
    execute: t.partial({
      // only one 'arg'
      inArguments: t.tuple([InArgsV1]),
      outArguments: t.unknown,
      url: t.string,
      body: t.string,
      header: t.string,
      verb: t.literal('POST'),
      format: t.literal('json'),
      useJwt: t.boolean,
      timeout: t.number
    })
  }),
  metaData: t.partial({
    isConfigured: t.boolean,
    // custom properties, nested under atomic_io to avoid clashes with any SFMC properties
    atomic_io: t.partial({
      organisationId: t.string,
      environmentId: t.string,
      actionFlowId: t.string
    })
  })

  // may want to know about these too
  // editable: true,
  // errors: null
})
export type Payload = t.TypeOf<typeof ConfiguredPayloadCodecV1>

/* Migration is from a shape like this (other properties omitted): 
{
  arguments: {
    execute: {
      inArguments: [ // everything is an entry in this array with 1 k/v pair
        // old format args
        {organisationId: 'some-org-id'},
        {environmentId: 'some-env-id'},
        {actionFlowId: 'some-flow-id'},
        {name: '{{somenamekey}}'}, // mapped variable
        {targetUserId: 'someuser'}
      ],
    }
  },
  metaData: {
    ...otherprops
  },
}

to something like this:
{
  arguments: {
    execute: {
      inArguments: [{ // one entry in this array
          variables: {
            name: '{{somenamekey}}'
          }, // variables now live here
          targetUserIds: ['someuser'], // user id in array
        }],
    }
  },
  metaData: {
    atomic_io: { // configuration-only props now live in metadata
      organisationId: 'some-org-id'
      environmentId: 'some-env-id'
      actionFlowId: 'some-flow-id'
    }
  },
}
*/
export const migratePayload = (data: any): Either<t.Errors, Payload> => {
  // return nestVars(moveMetaData(data))
  const valid = t
    .union([ConfiguredPayloadCodecV1, PayloadCodecV0, UnConfiguredConfigCodec])
    .decode(data)

  if (isLeft(valid)) return valid

  const migrated = migrateToV1(moveMetaData(valid.right))
  return right(migrated)
}

/** We've moved these fields from `inArguments` to `metaData`.
 * If the configuration is already stored in metaData then keep it otherwise look in the inArguments */
const moveMetaData = (payload: PayloadV0 | Payload): Payload | PayloadV0 => {
  const fromMetadata = payload.metaData?.atomic_io ?? {}
  if (
    // must already have been set - leave args as-is
    fromMetadata.organisationId ||
    fromMetadata.environmentId ||
    fromMetadata.actionFlowId ||
    // if we're using a new schema shape then we wont find metadata here
    !!payload.arguments?.execute?.inArguments?.[0]?.schemaVersion
  ) {
    return payload
  }

  // see if we find something here
  const inArguments = (
    (payload.arguments?.execute?.inArguments as Record<string, string>[]) ?? []
  ).reduce(
    (acc: Record<string, string>, obj) => ({
      ...acc,
      ...obj
    }),
    {} as Record<string, string>
  )

  const {organisationId, environmentId, actionFlowId} = inArguments

  const moved = set(payload, 'metaData.atomic_io', {
    ...payload.metaData?.atomic_io,
    organisationId,
    environmentId,
    actionFlowId
  })

  const cleanArgs = (
    payload.arguments?.execute?.inArguments ?? ([] as any[])
  ).filter(argObj => {
    // see the migration comment above for details
    const [key] = Object.entries(argObj)[0]
    return (
      key !== 'organisationId' &&
      key !== 'environmentId' &&
      key !== 'actionFlowId'
    )
  })

  // remove the old values
  return set(moved, 'arguments.execute.inArguments', cleanArgs)
}

const migrateToV1 = (state: Payload | PayloadV0): Payload => {
  const inArgs = state.arguments?.execute?.inArguments
  // probably the first time we've seen this activity no update needed, just fallback to default
  if (!inArgs?.length)
    return {
      ...state,
      arguments: {
        ...state.arguments,
        execute: {
          ...state.arguments?.execute,
          inArguments: [defaultInArgs]
        }
      }
    }

  // if we've migrated before (or this is the latest 'config.jsonc')
  if (
    state.arguments?.execute?.inArguments?.length === 1 &&
    state.arguments.execute.inArguments[0].schemaVersion === '1'
  )
    return state as Payload

  // move arguments into the new nested shape
  const argsAsObj = argsToState(inArgs as Record<string, string>[])
  const {targetUserId, ...restArgs} = argsAsObj

  return {
    ...state,
    arguments: {
      ...state.arguments,
      execute: {
        ...state.arguments?.execute,
        inArguments: [
          {
            schemaVersion: '1',
            variables: restArgs,
            // array of users
            targetUserIds: targetUserId ? [targetUserId] : []
          }
        ]
      }
    }
  }
}

const argsToState = (
  inArgs: {[key: string]: string}[] | undefined
): Record<string, string> =>
  inArgs?.reduce(
    (acc, obj) => ({
      ...acc,
      ...obj
    }),
    {}
  ) ?? {}

export const defaultInArgs: InArgsV1 = {
  variables: {},
  targetUserIds: [],
  schemaVersion: '1'
}
